import type { DocumentRecord, Message, Policy, SignPurpose, SignRequest } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, ExternalLink, Link2, MessageSquare, PenLine, Plus, ShieldOff } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, Input, Select, Sheet } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { shortDate } from "../theme";

/** Customer file: issue the self-service link, and request or follow electronic signatures. */

type SignRow = { request: SignRequest; requestedBy: string; path: string; signedUrl: string | null };
const PURPOSES: SignPurpose[] = ["um_uim_rejection", "pip_rejection", "application", "authorization", "other"];

export function CustomerSelfService({ customerId, policies, documents }: { customerId: string; policies: Policy[]; documents: DocumentRecord[] }) {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [link, setLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [requesting, setRequesting] = useState(false);
  const signs = useQuery({ queryKey: ["sign-requests", customerId], queryFn: async () => (await callAction<"agency.sign.list", SignRow[]>("agency.sign.list", { customerId, status: "all", limit: 50 })).result });
  const fail = (e: unknown) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" });
  const issue = useMutation({ mutationFn: () => callAction<"agency.portal.link", { url: string; expiresAt: string }>("agency.portal.link", { customerId, days: 30 }), onSuccess: (r) => setLink(r.result), onError: fail });
  const revoke = useMutation({ mutationFn: () => callAction("agency.portal.revoke", { customerId }), onSuccess: () => { setLink(null); toast({ title: t("selfservice.link.revoked"), tone: "success" }); }, onError: fail });
  const textLink = useMutation({
    mutationFn: (body: string) => callAction<"agency.message.send", Message>("agency.message.send", { customerId, channel: "sms", body, category: "transactional", aiDrafted: false }),
    onSuccess: (r) => { toast({ title: `${t("faraday.sentStatus")}: ${t(`inbox.status.${r.result.status}`)}`, tone: "success" }); qc.invalidateQueries({ queryKey: ["customer"] }); },
    onError: fail,
  });
  const cancel = useMutation({ mutationFn: (id: string) => callAction("agency.sign.cancel", { signRequestId: id }), onSuccess: () => { toast({ title: t("selfservice.sign.cancelled"), tone: "success" }); qc.invalidateQueries({ queryKey: ["sign-requests", customerId] }); }, onError: fail });
  const copy = async (url: string) => { try { await navigator.clipboard.writeText(url); toast({ title: t("selfservice.link.copied"), tone: "success" }); } catch { /* clipboard blocked */ } };
  const origin = window.location.origin;

  return (
    <Card>
      <CardHeader title={t("selfservice.card")} />
      <div className="space-y-4 px-5 pb-5">
        <div>
          <p className="font-semibold">{t("selfservice.link")}</p>
          <p className="text-sm text-ink/60">{t("selfservice.link.hint")}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => issue.mutate()} loading={issue.isPending}><Link2 className="h-4 w-4" />{t("selfservice.link.issue")}</Button>
            {link ? (
              <>
                <code className="max-w-full truncate rounded-lg bg-ink/5 px-2 py-1 text-xs">{link.url}</code>
                <Button size="sm" variant="outline" onClick={() => copy(link.url)}><Copy className="h-4 w-4" />{t("selfservice.link.copy")}</Button>
                <Button size="sm" variant="outline" onClick={() => textLink.mutate(t("selfservice.link.textBody").replace("{agency}", config.agency.displayName).replace("{url}", link.url))} loading={textLink.isPending}><MessageSquare className="h-4 w-4" />{t("selfservice.link.text")}</Button>
                <a href={link.url} target="_blank" rel="noopener" className="inline-flex min-h-[36px] items-center gap-1 rounded-xl border border-ink/15 px-3 text-sm font-semibold hover:border-primary"><ExternalLink className="h-4 w-4" />{t("common.open")}</a>
                <span className="text-xs text-ink/50">{t("selfservice.link.valid")} {shortDate(link.expiresAt, locale)}</span>
              </>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => revoke.mutate()} loading={revoke.isPending}><ShieldOff className="h-4 w-4" />{t("selfservice.link.revoke")}</Button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <p className="font-semibold">{t("selfservice.sign")}</p>
            <Button size="sm" variant="outline" onClick={() => setRequesting(true)}><Plus className="h-4 w-4" />{t("selfservice.sign.request")}</Button>
          </div>
          {signs.data && signs.data.length === 0 ? <p className="mt-1 text-sm text-ink/55">{t("selfservice.sign.none")}</p> : null}
          <ul className="mt-2 divide-y divide-ink/5">
            {(signs.data ?? []).map(({ request: r, requestedBy, path, signedUrl }) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <Badge tone={r.status === "signed" ? "green" : r.status === "pending" ? "amber" : r.status === "declined" ? "red" : "neutral"}>{t(`sign.status.${r.status}`)}</Badge>
                <span className="font-semibold">{r.title}</span>
                <span className="text-xs text-ink/55">{shortDate(r.createdAt, locale)} · {requestedBy}{r.signerName ? ` · ${r.signerName}` : ""}</span>
                <span className="ml-auto flex flex-wrap gap-1">
                  {r.status === "pending" ? (
                    <>
                      <Link to={path} target="_blank" className="inline-flex min-h-[32px] items-center gap-1 rounded-lg border border-ink/15 px-2 text-xs font-semibold hover:border-primary"><PenLine className="h-3.5 w-3.5" />{t("selfservice.sign.openNow")}</Link>
                      <Button size="sm" variant="outline" onClick={() => copy(`${origin}${path}`)}><Copy className="h-3.5 w-3.5" />{t("selfservice.link.copy")}</Button>
                      <Button size="sm" variant="outline" onClick={() => textLink.mutate(t("selfservice.sign.textBody").replace("{title}", r.title).replace("{agency}", config.agency.displayName).replace("{url}", `${origin}${path}`))} loading={textLink.isPending}><MessageSquare className="h-3.5 w-3.5" />{t("selfservice.sign.textLink")}</Button>
                      <Button size="sm" variant="ghost" onClick={() => cancel.mutate(r.id)}>{t("selfservice.sign.cancel")}</Button>
                    </>
                  ) : null}
                  {signedUrl ? <a href={signedUrl} target="_blank" rel="noopener" className="inline-flex min-h-[32px] items-center gap-1 rounded-lg border border-ink/15 px-2 text-xs font-semibold hover:border-primary"><ExternalLink className="h-3.5 w-3.5" />{t("selfservice.sign.signedDoc")}</a> : null}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-ink/50">{t("selfservice.sign.legal")}</p>
        </div>
      </div>
      <SignRequestSheet open={requesting} onClose={() => setRequesting(false)} customerId={customerId} policies={policies} documents={documents.filter((d) => d.contentType === "application/pdf")} />
    </Card>
  );
}

function SignRequestSheet({ open, onClose, customerId, policies, documents }: { open: boolean; onClose: () => void; customerId: string; policies: Policy[]; documents: DocumentRecord[] }) {
  const t = useT();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({ purpose: "um_uim_rejection" as SignPurpose, policyId: policies.find((p) => p.line === "auto")?.id ?? "", documentId: "", choice: "0", limits: "" });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const generated = form.purpose === "um_uim_rejection" || form.purpose === "pip_rejection";
  const create = useMutation({
    mutationFn: () => callAction<"agency.sign.request", { url: string; path: string }>("agency.sign.request", { customerId, policyId: form.policyId || undefined, purpose: form.purpose, documentId: generated ? undefined : form.documentId, selections: generated ? { choice: form.choice, limits: form.limits } : {}, days: 14 }),
    onSuccess: () => { toast({ title: t("selfservice.sign.created"), tone: "success" }); qc.invalidateQueries({ queryKey: ["sign-requests", customerId] }); qc.invalidateQueries({ queryKey: ["customer"] }); onClose(); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  return (
    <Sheet open={open} onClose={onClose} title={t("selfservice.sign.request")}>
      <form className="space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (generated || form.documentId) create.mutate(); }}>
        <Select label={t("selfservice.sign.purpose")} value={form.purpose} onChange={set("purpose")}>{PURPOSES.map((p) => <option key={p} value={p}>{t(`selfservice.sign.purpose.${p}`)}</option>)}</Select>
        <Select label={t("selfservice.sign.policy")} value={form.policyId} onChange={set("policyId")}>
          <option value="">—</option>
          {policies.map((p) => <option key={p.id} value={p.id}>{p.carrier} {p.line.replace(/_/g, " ")} {p.policyNumber}</option>)}
        </Select>
        {generated ? (
          <>
            <Select label={t("selfservice.sign.choice")} value={form.choice} onChange={set("choice")}>
              <option value="0">{t("selfservice.sign.choice.0")}</option>
              <option value="1">{t("selfservice.sign.choice.1")}</option>
              {form.purpose === "um_uim_rejection" ? <option value="2">{t("selfservice.sign.choice.2")}</option> : null}
            </Select>
            <Input label={t("selfservice.sign.limits")} value={form.limits} onChange={set("limits")} placeholder="30/60/25" />
          </>
        ) : (
          <Select label={t("selfservice.sign.document")} value={form.documentId} onChange={set("documentId")} required>
            <option value="">—</option>
            {documents.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </Select>
        )}
        <p className="text-xs text-ink/50">{t("selfservice.sign.legal")}</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" loading={create.isPending}><PenLine className="h-4 w-4" />{t("selfservice.sign.create")}</Button>
        </div>
      </form>
    </Sheet>
  );
}
